import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import NotificationDropdown from './NotificationDropdown';
import { Layers } from 'lucide-react';
import { FaRegUserCircle } from 'react-icons/fa';
import { useDispatch } from 'react-redux'; // Import useDispatch
import { userUpdateViewType } from '../../app/reducers/userInfo/slice'; // Import userUpdateViewType
// import { notifications } from '../../Data/Dummydata';
import { useSelector } from 'react-redux';
import { userLogout } from '../../app/reducers/userInfo/slice';
import { clearBrand } from '../../app/reducers/myBrand/slice';
import { toast } from 'react-toastify';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, viewType } = useSelector((state) => state.userInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    dispatch(userLogout());
    dispatch(clearBrand());
    toast.success('Logged Out Successfully');
    navigate('/login');
    setShowLogoutModal(false);
  };

  const handleToggleView = () => {
    const newViewType = viewType === 'seller' ? 'brand' : 'seller';
    dispatch(userUpdateViewType(newViewType));
    if (newViewType === 'brand') {
      navigate('/brand/products');
    } else if (newViewType === 'seller') {
      navigate('/seller/brand-catalog');
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const path = location.pathname;

    // if (user.role === 'brand' && path.startsWith('/seller')) {
    //   navigate('/brand/dashboard');
    // } else if (user.role === 'seller' && path.startsWith('/brand')) {
    //   navigate('/seller/dashboard');
    // }
    if (viewType === 'brand' && path.startsWith('/seller')) {
      navigate('/brand/products');
    } else if (viewType === 'seller' && path.startsWith('/brand')) {
      navigate('/seller/brand-catalog');
    }
  }, [user, location.pathname, navigate, viewType]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showNotifications && !e.target.closest('.notification-container')) {
        setShowNotifications(false);
      }

      if (showDropdown && !e.target.closest('.profile-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showNotifications, showDropdown]);

  useEffect(() => {
    setShowDropdown(false);
    setShowNotifications(false);
    setSidebarOpen(false);
  }, [location.pathname]);

  if (!user) {
    return null;
  }

  // const handleNotificationClick = () => {
  //   navigate('seller/notification');
  // };

  const goToProfile = () => {
    const routeMap = {
      brand: '/brand/profile',
      seller: '/seller/profile',
    };
    navigate(routeMap[viewType] || '/');
  };

  return (
    <div className="flex h-screen bg-gray-50" data-route={location.pathname}>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-white transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar role={user.role} closeSidebar={() => setSidebarOpen(false)} />
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="secondary-background shadow-sm">
          <div className="flex h-16 items-center justify-between px-4 md:px-6">
            <div className="flex items-center space-x-4">
              <button
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 md:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
              <Layers size={24} className="primary-color" />
            </div>

            <div className="flex items-center space-x-6">
              {/* View Type Toggle */}
              {(user.role || []).includes('brand') && (user.role || []).includes('seller') && (
                <div
                  className="flex items-center cursor-pointer bg-gray-200 rounded-full p-1"
                  onClick={handleToggleView}
                >
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full ${
                      viewType === 'seller' ? 'bg-primary text-white' : 'text-gray-700'
                    }`}
                  >
                    Seller
                  </span>
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full ${
                      viewType === 'brand' ? 'bg-primary text-white' : 'text-gray-700'
                    }`}
                  >
                    Brand
                  </span>
                </div>
              )}

              <div className="relative flex items-center space-x-6">
                <NotificationDropdown />

                <div className="relative profile-container">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="flex items-center space-x-2 rounded-full p-1 hover:bg-[#8324743D]"
                  >
                    <FaRegUserCircle className="h-6 w-6 primary-color" />
                  </button>

                  {showDropdown && (
                    <div className="absolute right-0 z-50 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="py-1">
                        <button
                          onClick={goToProfile}
                          className="block w-full px-4 py-2 text-sm text-left tertiary-color hover:bg-gray-100"
                        >
                          Your Profile
                        </button>

                        <button
                          onClick={() => setShowLogoutModal(true)}
                          className="block w-full px-4 py-2 text-sm text-left primary-color hover:bg-red-50"
                        >
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-primary scrollbar-track-transparent">
          <div className="mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-96 p-6 text-center mx-4">
            <h2 className="text-lg font-semibold  mb-4">Are you sure you want to logout?</h2>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="px-4 py-1 rounded-md border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-1 rounded-md primary-background white-text hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
